<template>
  <div v-if="show" class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50" @click.self="handleCancel">
    <div class="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white" @click.stop>
      <div class="mt-3">
        <!-- Modal Header -->
        <div class="flex items-center justify-between pb-4 border-b border-gray-200">
          <div class="flex items-center">
            <svg class="h-6 w-6 text-yellow-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <h3 class="text-lg font-medium text-gray-900">
              {{ $t('Wichtige Lieferinformationen') }}
            </h3>
          </div>
          <button
            @click="handleCancel"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Modal Body -->
        <div class="mt-4">
          <!-- Warning depends on whether partial delivery is enabled -->
          <div v-if="allowPartialDelivery" class="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
            <div class="flex">
              <div class="ml-3">
                <p class="text-sm font-medium text-red-800 mb-1">
                  {{ $t('Achtung: Deine gesamte Bestellung wird sich verzögern') }}
                </p>
                <p class="text-sm text-red-700">
                  {{ $t('Da du keine Teillieferung aktiviert hast, wird deine gesamte Bestellung erst versandt, wenn alle Artikel verfügbar sind.') }}
                </p>
                <p class="text-sm text-red-700 mt-2 font-medium">
                  {{ $t('Aktiviere Teillieferungen, um verfügbare Artikel früher zu erhalten') }}
                </p>
              </div>
            </div>
          </div>
          <div v-else class="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-6">
            <div class="flex">
              <div class="ml-3">
                <p class="text-sm font-medium text-yellow-800 mb-1">
                  {{ $t('Deine Bestellung wird in zwei Teillieferungen aufgeteilt') }}
                </p>
                <p class="text-sm text-yellow-700">
                  {{ $t('Diese Produkte sind derzeit nicht auf Lager und haben verlängerte Lieferzeiten. Sie werden separat versandt, sobald sie verfügbar sind.') }}
                </p>
              </div>
            </div>
          </div>

          <!-- Pre-Order Products Section -->
          <div class="mb-6">
            <div class="flex items-center mb-3">
              <svg class="h-5 w-5 text-red-500 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h4 class="text-sm font-semibold text-gray-900 uppercase tracking-wide">
                {{ $t('Produkte in Vorbestellung (verzögerte Lieferung)') }}
              </h4>
            </div>
            <div class="max-h-60 overflow-y-auto border border-red-200 rounded-md">
              <table class="min-w-full divide-y divide-gray-200">
                <thead class="bg-red-50">
                  <tr>
                    <th class="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {{ $t('Produkt') }}
                    </th>
                    <th class="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {{ $t('Menge') }}
                    </th>
                    <th class="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {{ $t('Voraussichtliche Lieferung') }}
                    </th>
                  </tr>
                </thead>
                <tbody class="bg-white divide-y divide-gray-200">
                  <tr v-for="product in productsWithLongDelivery" :key="product.sku">
                    <td class="px-4 py-3 text-sm text-gray-900">
                      <div class="font-medium">{{ product.name }}</div>
                      <div class="text-xs text-gray-500">{{ product.sku }}</div>
                    </td>
                    <td class="px-4 py-3 text-sm text-gray-900 text-center">
                      {{ product.quantity }}
                    </td>
                    <td class="px-4 py-3 text-sm text-right">
                      <!-- Partially-available line: show the real split (X ships now, Y becomes a
                           pre-order), matching the inline cart message and the backend exactly. -->
                      <div v-if="splitOf(product).isSplit" class="text-sm font-medium text-amber-700">
                        {{ $t(':available sofort lieferbar, :rest als Vorbestellung', { available: splitOf(product).available, rest: splitOf(product).rest }) }}
                      </div>
                      <!-- Fully out-of-stock line: keep the estimated delivery date wording. -->
                      <div v-else class="text-sm font-semibold text-red-700">
                        {{ formatEstimatedDate(product.deliveryInWeeksFrom) }}
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Information Message (only shown when partial delivery is activated) -->
          <div v-if="!allowPartialDelivery" class="bg-blue-50 border border-blue-200 rounded-md p-4">
            <div class="flex">
              <div class="flex-shrink-0">
                <svg class="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd" />
                </svg>
              </div>
              <div class="ml-3">
                <h3 class="text-sm font-medium text-blue-800">
                  {{ $t('Was das für deine Bestellung bedeutet') }}
                </h3>
                <div class="mt-2 text-sm text-blue-700">
                  <p class="mb-2">{{ $t('Deine Bestellung wird automatisch in zwei separate Bestellungen aufgeteilt:') }}</p>
                  <ul class="list-disc list-inside space-y-1">
                    <li>{{ $t('Reguläre Bestellung: Verfügbare Artikel werden wie gewohnt verarbeitet und versandt') }}</li>
                    <li>{{ $t('Vorbestellung: Artikel mit langen Lieferzeiten werden separat verarbeitet und später versandt') }}</li>
                    <li>{{ $t('Beide Bestellungen erscheinen in deinem Bestellverlauf') }}</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Modal Footer -->
        <div class="mt-6 flex justify-between">
          <button
            @click="handleCancel"
            class="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sonnenglas-500"
          >
            {{ $t('Zurück') }}
          </button>
          <button
            @click="handleConfirm"
            class="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-sonnenglas-600 hover:bg-sonnenglas-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-sonnenglas-500"
          >
            {{ $t('Mit Bestellung fortfahren') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getCurrentInstance } from 'vue'
import { splitQuantityByAvailability, type AvailabilitySplit } from '@/utils/deliveryAvailability'

interface ProductWithDelivery {
  sku: string
  name: string
  quantity: number
  deliveryInWeeksFrom?: number
  deliveryInWeeksTo?: number
  availableQuantity?: number | null
}

interface Props {
  show: boolean
  productsWithLongDelivery: ProductWithDelivery[]
  allowPartialDelivery?: boolean
}

interface Emits {
  (e: 'confirm'): void
  (e: 'cancel'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// Get the global $t function from Vue instance
const instance = getCurrentInstance()
const t = instance?.appContext.config.globalProperties.$t || ((key: string) => key)

const handleConfirm = () => {
  emit('confirm')
}

const handleCancel = () => {
  emit('cancel')
}

// Per-line split: how much of this line ships immediately vs. becomes a pre-order. Uses the same
// helper as the inline cart message so the modal and the cart agree (and match the backend).
const splitOf = (product: ProductWithDelivery): AvailabilitySplit => {
  return splitQuantityByAvailability(product.quantity, product.availableQuantity)
}

const formatEstimatedDate = (weeks?: number): string => {
  if (!weeks) return ''

  // For weeks >= 8, show "Lieferung ca X Monate"
  if (weeks >= 8) {
    const months = Math.floor(weeks / 4)
    const monthsUnit = months > 1 ? t('Monate') : t('Monat')
    return `${t('Lieferung')} ca ${months} ${monthsUnit}`
  }

  // For shorter periods, show the estimated month/year
  const date = new Date()
  date.setDate(date.getDate() + (weeks * 7))

  const options: Intl.DateTimeFormatOptions = {
    month: 'long',
    year: 'numeric'
  }

  const locale = document.documentElement.lang || 'de-DE'
  return `ca. ${date.toLocaleDateString(locale, options)}`
}
</script>